import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  Award, Play, Loader2, Download, CheckCircle, XCircle, AlertCircle,
  ChevronDown, ChevronRight, ShieldCheck, Clock, FileBadge, Activity, RefreshCw,
  ArrowRight, ArrowLeft
} from 'lucide-react';
import { certificationApi } from '../../services/iso20022Api';
import { useAuth } from '../../contexts/AuthContext';
import CertCaseModal from './CertCaseModal';
import { hasBeenExercised, decisionLabel, finalizeBlocker, verdictSummary, runModeLabel, credentialLabel } from '../../utils/certification';

const STATUS_STYLE = {
  PASS:             { bg: 'bg-green-100',  tx: 'text-green-800',  label: 'PASS' },
  CONDITIONAL_PASS: { bg: 'bg-amber-100',  tx: 'text-amber-800',  label: 'CONDITIONAL PASS' },
  FAIL:             { bg: 'bg-red-100',    tx: 'text-red-800',    label: 'FAIL' },
  INCOMPLETE:       { bg: 'bg-gray-200',   tx: 'text-gray-700',   label: 'INCOMPLETE' },
};

const RESULT_STYLE = {
  PASS:             'bg-green-100 text-green-800',
  CONDITIONAL_PASS: 'bg-amber-100 text-amber-800',
  FAIL:             'bg-red-100 text-red-800',
  NOT_RUN:          'bg-gray-100 text-gray-500',
};

const CASE_ICON = {
  PASS:    <CheckCircle className="w-4 h-4 text-green-500" />,
  FAIL:    <XCircle className="w-4 h-4 text-red-500" />,
  BLOCKED: <Clock className="w-4 h-4 text-gray-400" />,
  SKIPPED: <Clock className="w-4 h-4 text-gray-400" />,
};

/** A run in flight polls this often; it finishes in well under a minute. */
const AUTO_POLL_MS = 2000;

// Case-detail grouping: mandatory tests split by traffic direction, then optional.
const CASE_GROUPS = [
  {
    key: 'mandatory-inbound',
    title: 'Mandatory — inbound tests',
    subtitle: 'participant → switch: your gateway sends signed messages to the switch endpoints',
    icon: ArrowRight,
    accent: 'text-primary-700 bg-primary-50 border-primary-100',
    match: c => c.obligation === 'MANDATORY' && c.direction !== 'OUTBOUND',
  },
  {
    key: 'mandatory-outbound',
    title: 'Mandatory — outbound tests',
    subtitle: 'switch → participant: the switch delivers signed callbacks to your configured endpoints',
    icon: ArrowLeft,
    accent: 'text-violet-700 bg-violet-50 border-violet-100',
    match: c => c.obligation === 'MANDATORY' && c.direction === 'OUTBOUND',
  },
  {
    key: 'optional',
    title: 'Optional',
    subtitle: 'recommended groundwork and extra flows — not required for certification',
    icon: FileBadge,
    accent: 'text-gray-600 bg-gray-50 border-gray-100',
    match: c => c.obligation !== 'MANDATORY',
  },
];

function Field({ label, value, mono }) {
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
      <p className={`text-sm text-gray-900 ${mono ? 'font-mono break-all' : ''}`}>{value || '—'}</p>
    </div>
  );
}

export default function Certification() {
  const { user } = useAuth();
  const [form, setForm] = useState({
    legalName: '', fspId: '', tenantId: '', contact: '',
    targetScheme: 'IIPS (readiness)',
  });
  const [report, setReport]   = useState(null);
  // The credential the report names, and whether it is an older report's placeholder.
  const [credential, setCredential] = useState({ value: null, note: null });
  useEffect(() => {
    let live = true;
    credentialLabel(report).then((c) => live && setCredential(c)).catch(() => {});
    return () => { live = false; };
  }, [report]);
  const [running, setRunning] = useState(false);
  const [error, setError]     = useState(null);
  const [openCases, setOpenCases] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);   // report case shown in the detail modal
  // 'live' = persisted participant-driven run, 'finalized' = issued report, 'runner' = on-demand self-test
  const [source, setSource]   = useState('live');
  const [loadingProgress, setLoadingProgress] = useState(true);
  const [finalizing, setFinalizing] = useState(false);
  const [notice, setNotice]   = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingCert, setDownloadingCert] = useState(false);

  // Automated run. `mayAutoRun` stays null until the engine answers: the flag
  // lives in the ISO engine's users table, and the session's own user object
  // comes from the FSPIOP login, so asking the engine is the only reliable
  // answer. A 403 from the status probe is what means "not enabled".
  const [mayAutoRun, setMayAutoRun] = useState(null);
  const [runStatus, setRunStatus]   = useState(null);
  const [autoStarting, setAutoStarting] = useState(false);
  const pollRef = useRef(null);

  // On open, show the tenant's live, persisted certification progress.
  const loadProgress = async () => {
    setLoadingProgress(true); setError(null);
    try {
      const prog = await certificationApi.progress();
      // BLOCKED does not mean the participant did anything: ISO-SEC-001 is
      // blocked on Sandbox mTLS and reports that way in every run, including a
      // brand-new empty one. Counting it as activity made this test always true,
      // so after a finalize the page showed the fresh, empty run — 0/12 NOT RUN —
      // instead of the passing report that had just been issued.
      const exercised = hasBeenExercised(prog);
      if (exercised) {
        setReport(prog); setSource('live'); setNotice(null);
      } else {
        // Active run is empty (e.g. just after a finalize) — show the last issued report if one exists.
        const latest = await certificationApi.latestReport().catch(() => null);
        if (latest) {
          setReport(latest); setSource('finalized');
          setNotice('Showing your last issued report. Send new messages to the Sandbox, then Refresh to track a fresh attempt.');
        } else {
          setReport(prog); setSource('live'); setNotice(null);
        }
      }
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoadingProgress(false);
    }
  };

  useEffect(() => { loadProgress(); }, []);

  // The poller outlives any one render, so it reaches the current loadProgress
  // through a ref rather than closing over the one that existed when it started.
  const loadProgressRef = useRef(loadProgress);
  loadProgressRef.current = loadProgress;

  const pollAutoRun = useCallback(async () => {
    try {
      const s = await certificationApi.autoRunStatus();
      setRunStatus(s);
      if (s.state !== 'RUNNING' && s.state !== 'FINALIZING') {
        clearInterval(pollRef.current);
        pollRef.current = null;
        await loadProgressRef.current();
      }
    } catch {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  // Asks the engine whether this account may auto-run, and reattaches to a run
  // already in flight — the page may have been reloaded mid-run.
  useEffect(() => {
    certificationApi.autoRunStatus()
      .then((s) => {
        setMayAutoRun(true);
        setRunStatus(s);
        if (s.state === 'RUNNING' || s.state === 'FINALIZING') {
          pollRef.current = setInterval(pollAutoRun, AUTO_POLL_MS);
        }
      })
      .catch(() => setMayAutoRun(false));
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [pollAutoRun]);

  const startAutoRun = async () => {
    setAutoStarting(true); setError(null); setNotice(null);
    try {
      setRunStatus(await certificationApi.startAutoRun());
      if (!pollRef.current) pollRef.current = setInterval(pollAutoRun, AUTO_POLL_MS);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Could not start the certification run');
    } finally {
      setAutoStarting(false);
    }
  };

  // Pre-fill the self-test form with the logged-in DFSP's details (without clobbering edits).
  useEffect(() => {
    const t = report?.tenant;
    if (!t && !user) return;
    setForm(f => ({
      ...f,
      legalName: f.legalName || t?.legalName || '',
      fspId:     f.fspId     || t?.fspId     || '',
      tenantId:  f.tenantId  || t?.tenantId  || '',
      contact:   f.contact   || user?.email  || '',
    }));
  }, [report, user]);

  const finalize = async () => {
    if (!window.confirm(
      `Finalize and issue the signed certification report?\n\n${verdictSummary(report)}.\n\n`
      + 'This closes the current run and sends its report to an AfricaNenda authorising officer. A fresh attempt starts afterward.'
    )) return;
    setFinalizing(true); setError(null);
    try {
      setReport(await certificationApi.finalize());
      setSource('finalized');
      setNotice('Report issued and the run was closed. A new attempt has started — use Refresh to track it.');
    } catch (e) {
      // The engine says why in `error` when the run cannot be finalized yet.
      setError(e.response?.data?.message || e.response?.data?.error || e.message);
    } finally {
      setFinalizing(false);
    }
  };

  const run = async () => {
    setRunning(true); setError(null); setNotice(null);
    try {
      setReport(await certificationApi.run(form));
      setSource('runner');
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setRunning(false);
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `${report.reportId}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const downloadPack = async () => {
    setDownloading(true); setNotice(null);
    try {
      const { blob, filename } = await certificationApi.downloadInstructions();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setNotice('Could not download the certification pack. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const downloadCertificate = async () => {
    setDownloadingCert(true); setNotice(null);
    try {
      const { blob, filename } = await certificationApi.downloadCertificate();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch {
      setNotice('Could not download the certificate. Please try again.');
    } finally {
      setDownloadingCert(false);
    }
  };

  const autoRunning = runStatus
    && (runStatus.state === 'RUNNING' || runStatus.state === 'FINALIZING');
  const st = report ? (STATUS_STYLE[report.status] || STATUS_STYLE.INCOMPLETE) : null;

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Award className="w-6 h-6 text-primary-600" /> Certification
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          Prove your integration is ready: the AfricaNenda suite checks that your gateway can
          exchange signed ISO 20022 messages with the switch — send a correctly signed request and
          accept the switch's signed response — then issues a signed, tamper-evident report. See{' '}
          <Link to="/iso20022/certification/scope" className="text-primary-600 hover:underline font-medium">
            Certification scope
          </Link>{' '}
          for exactly which tests lead to certification.
        </p>
      </div>

      {/* How it works */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-900 mb-3">How certification works</h2>
        <ol className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            ['1 · Practice', 'Use Self-Test for a quick, unscored dry run, and the Message Generator / Flow Simulator to see what correct messages look like.'],
            ['2 · Exercise', 'Point your payment gateway at the Sandbox and send real signed messages. Each corridor flow you exercise turns a case green below — or run the built-in suite for an instant result.'],
            ['3 · Certify', 'When the mandatory cases pass, Finalize & issue. You receive a signed report an AfricaNenda authorising officer can countersign.'],
          ].map(([step, text]) => (
            <li key={step} className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-primary-700">{step}</p>
              <p className="text-xs text-gray-600 mt-1">{text}</p>
            </li>
          ))}
        </ol>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
          <span className="flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5 text-green-500" /> PASS — the check succeeded</span>
          <span className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5 text-red-500" /> FAIL — needs a fix on your side</span>
          <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5 text-gray-400" /> BLOCKED — waiting on Sandbox infrastructure (mTLS); not counted against you</span>
        </div>
      </div>

      {/* Certification instructions pack — how to run every test */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <FileBadge className="w-4 h-4 text-primary-600" /> Certification instructions
          </h2>
          <p className="text-xs text-gray-500 mt-1 max-w-lg">
            Download a pack with every certification test — request URLs, expected results, and
            ready-to-send sample messages signed with your certificate — as a Postman collection,
            Markdown &amp; PDF guides, and per-case sample files.
          </p>
        </div>
        <button onClick={downloadPack} disabled={downloading}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 shrink-0">
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          {downloading ? 'Preparing…' : 'Download certification pack'}
        </button>
      </div>

      {/* Automated run — only for accounts the CoE has enabled for it */}
      {mayAutoRun && (
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-primary-600" /> Run all test scenarios
              </h2>
              <p className="text-xs text-gray-500 mt-1 max-w-lg">
                Issues every certifying exchange in the suite against the switch, signed with your
                certificate, then finalizes the run. It does not shortcut the credential — the
                certificate is still issued only after an AfricaNenda authorising officer approves.
              </p>
            </div>
            <button onClick={startAutoRun} disabled={autoRunning || autoStarting}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 shrink-0">
              {autoRunning || autoStarting
                ? <><Loader2 className="w-4 h-4 animate-spin" /> Running…</>
                : <><Play className="w-4 h-4" /> Run all test scenarios</>}
            </button>
          </div>

          {runStatus && runStatus.state !== 'IDLE' && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs text-gray-600">{runStatus.message}</p>
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-700">
                  {runStatus.state}
                </span>
              </div>
              {runStatus.totalCases > 0 && (
                <div className="w-full bg-gray-100 rounded-full h-2 mb-3">
                  <div className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{ width: `${Math.round((runStatus.casesIssued / runStatus.totalCases) * 100)}%` }} />
                </div>
              )}
              {(runStatus.outcomes || []).length > 0 && (
                <div className="text-xs font-mono space-y-1 max-h-52 overflow-y-auto">
                  {runStatus.outcomes.map(o => (
                    <div key={o.caseId} className="flex items-start gap-2">
                      <span className="text-gray-400 w-24 shrink-0">{o.caseId}</span>
                      <span className={o.execution === 'ERROR' ? 'text-red-600' : 'text-gray-600'}>
                        {o.detail || o.execution}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Live progress — the participant-driven observation run */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
              <Activity className="w-4 h-4 text-primary-600" /> Live certification progress
            </h2>
            <p className="text-xs text-gray-500 mt-1 max-w-lg">
              Updated as your payment gateway sends signed messages to the Sandbox endpoints.
              Cases you haven’t exercised yet show as not run.
              {source === 'live' && !hasBeenExercised(report) &&
                ' Finalize appears once a case has been exercised.'}
            </p>
            {source === 'live' && hasBeenExercised(report) && finalizeBlocker(report) && (
              <p className="text-xs text-amber-700 mt-1 max-w-lg">Not ready to finalize: {finalizeBlocker(report)}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={loadProgress} disabled={loadingProgress || finalizing}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              {loadingProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
            </button>
            {/* Nothing scored means there is no run to freeze: finalizing would
                issue a report for messages nobody sent. */}
            {source === 'live' && hasBeenExercised(report) && (
              <button onClick={finalize} disabled={finalizing || loadingProgress || Boolean(finalizeBlocker(report))}
                title={finalizeBlocker(report) || undefined}
                className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed">
                {finalizing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileBadge className="w-4 h-4" />} Finalize &amp; issue
              </button>
            )}
          </div>
        </div>
        {notice && (
          <div className="mt-3 text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
            {notice}
          </div>
        )}
      </div>

      {/* On-demand self-test (advanced) */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-gray-900">Run on-demand self-test (advanced)</h2>
        <p className="text-xs text-gray-500 mt-1 mb-3">
          Runs the engine’s built-in suite against the in-engine switch simulator instead of scoring
          the messages your gateway sends. Useful for a quick dry run.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            ['legalName', 'Legal name', 'Liberia Demo Bank Ltd.'],
            ['fspId', 'FSP ID', 'LIBDFSP01'],
            ['tenantId', 'Tenant ID', 'tnt_8f3c12a9'],
            ['contact', 'Contact email', 'integration@bank.example'],
            ['targetScheme', 'Target scheme', 'IIPS (readiness)'],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <label className="block text-xs text-gray-500 mb-1">{label}</label>
              <input value={form[k]} placeholder={ph}
                onChange={e => setForm(f => ({ ...f, [k]: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm" />
            </div>
          ))}
        </div>
        <button onClick={run} disabled={running || !form.fspId}
          className="mt-4 flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {running ? 'Running suite…' : 'Run certification'}
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {report && (
        <>
          {/* Report header band */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <p className="text-xs text-gray-400 uppercase tracking-wide">{report.issuedBy} · IIPS Sandbox</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <h2 className="text-xl font-bold text-gray-900">Certification Report</h2>
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                    {source === 'live' ? 'Live progress' : source === 'finalized' ? 'Finalized' : 'Self-test'}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {report.corridor?.name} · Executed on {report.executedOn}
                </p>
              </div>
              <div className="text-right">
                <span className={`inline-block text-sm font-semibold px-3 py-1.5 rounded-md ${st.bg} ${st.tx}`}>
                  {st.label}
                </span>
                <p className="font-mono text-xs text-gray-400 mt-2">{report.reportId}</p>
                <p className="text-xs text-gray-400">
                  {report.runWindow?.startedAt?.slice(11, 19)}–{report.runWindow?.completedAt?.slice(11, 19)} UTC
                </p>
              </div>
            </div>

            {/* info cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t border-gray-100">
              <Field label="Tenant" value={report.tenant?.legalName} />
              <Field label="Target scheme" value={report.targetScheme} />
              <Field label="Corridor" value={`${report.corridor?.name} v${report.corridor?.version}`} />
              <Field label="FSP ID" value={report.tenant?.fspId} mono />
            </div>
          </div>

          {/* Verdict & scorecard */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Verdict &amp; scorecard</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              {[
                ['Mandatory', `${report.scorecard.mandatory.passed}/${report.scorecard.mandatory.total}`],
                ['Optional', `${report.scorecard.optional.passed}/${report.scorecard.optional.total}`],
                ['Overall pass rate', `${Math.round(report.scorecard.overallPassRate * 1000) / 10}%`],
              ].map(([label, val]) => (
                <div key={label} className="bg-gray-50 rounded-lg p-3 text-center">
                  <p className="text-2xl font-bold text-gray-900">{val}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{label}</p>
                </div>
              ))}
            </div>
            <table className="w-full text-sm">
              <tbody>
                {(report.categories || []).map(c => (
                  <tr key={c.id} className="border-t border-gray-100">
                    <td className="py-2 text-gray-700">{c.name}</td>
                    <td className="py-2 text-right">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded ${RESULT_STYLE[c.result] || RESULT_STYLE.NOT_RUN}`}>
                        {c.result.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Defects */}
          {report.defects?.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Open defects ({report.defects.length})</h3>
              <div className="space-y-2">
                {report.defects.map(d => (
                  <div key={d.defectId} className="border-l-2 border-amber-400 bg-amber-50 rounded-r-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-mono text-xs text-amber-800">{d.defectId}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-200 text-amber-900">{d.severity}</span>
                      <span className="text-xs text-gray-500">· {d.relatedCase}</span>
                    </div>
                    <p className="text-sm font-medium text-amber-900">{d.summary}</p>
                    {d.detail && <p className="text-xs text-amber-800 mt-1">{d.detail}</p>}
                    {d.remediation && <p className="text-xs text-gray-600 mt-1"><span className="font-medium">Remediation:</span> {d.remediation}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Environment + governance/integrity */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Environment fingerprint</h3>
              <div className="space-y-2">
                <Field label="Mojaloop Hub" value={report.environment?.mojaloopHubVersion} mono />
                <Field label="Adapter" value={report.environment?.adapterVersion} mono />
                <Field label="Suite" value={`${report.environment?.suiteVersion}`} mono />
                <Field label="Suite hash" value={report.environment?.suiteHash} mono />
                <Field label="Your credential" value={credential.value} mono />
                {credential.note && <p className="text-[11px] text-gray-500 -mt-1">{credential.note}</p>}
                <Field label="Run" value={runModeLabel(report)} />
                <Field label="Sandbox CA" value={report.environment?.sandboxCa} mono />
              </div>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-gray-400" /> Governance &amp; integrity
              </h3>
              <div className="space-y-2">
                <Field label="Test collections" value={report.governance?.testCollectionsOwner} />
                <Field label="Engine" value={report.governance?.engineProvider} />
                <Field label="Suite reference" value={report.governance?.suiteReference} mono />
                <Field label="Result hash" value={report.integrity?.resultHash} mono />
                <Field label="Signed by" value={`${report.integrity?.signedBy} (${report.integrity?.signatureAlg})`} />
              </div>
            </div>
          </div>

          {/* Sign-off state */}
          {(() => {
            const so = report.signOff;
            const approved = so && (so.decision === 'APPROVED' || so.decision === 'APPROVED_WITH_CONDITIONS');
            // A revoked certificate no longer stands; its reason is the note on the decision.
            const rejected = so && (so.decision === 'REJECTED' || so.decision === 'REVOKED');
            const revoked = so && so.decision === 'REVOKED';
            return (
              <div className={`border rounded-xl p-4 flex items-center gap-3 flex-wrap ${
                approved ? 'bg-green-50 border-green-200'
                  : rejected ? 'bg-red-50 border-red-200'
                  : 'bg-blue-50 border-blue-200'
              }`}>
                <FileBadge className={`w-5 h-5 shrink-0 ${
                  approved ? 'text-green-600' : rejected ? 'text-red-600' : 'text-blue-600'
                }`} />
                <p className={`text-sm flex-1 min-w-48 ${
                  approved ? 'text-green-900' : rejected ? 'text-red-900' : 'text-blue-900'
                }`}>
                  {so
                    ? <>
                        {decisionLabel(so.decision)} by {so.authorisingOfficer}
                        {so.title ? ` (${so.title})` : ''}
                        {so.decisionAt ? ` on ${so.decisionAt.slice(0, 10)}` : ''}
                        {so.conditions?.length > 0 && <> — {so.conditions[0].condition}</>}
                        {revoked && <>. Finalize a new run to be certified again.</>}
                      </>
                    : 'Awaiting an AfricaNenda authorising officer’s sign-off. This result is machine-generated and tamper-evident; the officer records the final decision.'}
                </p>
                {approved && (
                  <>
                    <button onClick={downloadCertificate} disabled={downloadingCert}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg bg-green-600 text-white text-sm font-medium hover:bg-green-700 disabled:opacity-50 shrink-0">
                      {downloadingCert ? <Loader2 className="w-4 h-4 animate-spin" /> : <Award className="w-4 h-4" />}
                      Download certificate (PDF)
                    </button>
                    <Link to="/iso20022/deployment"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-green-300 text-green-800 text-sm font-medium hover:bg-green-100 shrink-0">
                      What production looks like <ChevronRight className="w-4 h-4" />
                    </Link>
                  </>
                )}
              </div>
            );
          })()}

          {/* Cases (expandable) */}
          <div className="bg-white border border-gray-200 rounded-xl">
            <button onClick={() => setOpenCases(o => !o)}
              className="w-full flex items-center justify-between p-4 text-left">
              <span className="text-sm font-semibold text-gray-900">
                Case detail ({report.cases.length})
                <span className="ml-2 text-xs font-normal text-gray-400">click a case for how to run it</span>
              </span>
              {openCases ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
            </button>
            {openCases && (
              <div className="px-4 pb-4 space-y-4">
                {CASE_GROUPS.map(g => {
                  const inGroup = report.cases.filter(g.match);
                  if (inGroup.length === 0) return null;
                  const passed = inGroup.filter(c => c.status === 'PASS').length;
                  const counted = inGroup.filter(c => c.status !== 'BLOCKED').length;
                  const GroupIcon = g.icon;
                  return (
                    <div key={g.key}>
                      <div className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${g.accent}`}>
                        <GroupIcon className="w-4 h-4 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold">{g.title}</p>
                          <p className="text-[11px] opacity-80">{g.subtitle}</p>
                        </div>
                        <span className="text-xs font-medium shrink-0">{passed}/{counted} passed</span>
                      </div>
                      <div className="mt-1 space-y-1">
                        {inGroup.map(c => {
                          const blockedReason = c.status === 'BLOCKED'
                            ? c.assertions?.find(a => a.observed)?.observed
                            : null;
                          return (
                            <button key={c.caseId} onClick={() => setSelectedCase(c)}
                              className="w-full text-left py-1.5 border-t border-gray-50 rounded-md
                                         hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400">
                              <div className="flex items-start gap-2">
                                {CASE_ICON[c.status] || CASE_ICON.SKIPPED}
                                <span className="font-mono text-xs text-gray-400 w-24 shrink-0">{c.caseId}</span>
                                <span className="flex-1 text-sm text-gray-700">{c.title}</span>
                                <span className={`text-xs font-medium px-1.5 py-0.5 rounded ${
                                  c.status === 'PASS' ? 'bg-green-100 text-green-700'
                                    : c.status === 'FAIL' ? 'bg-red-100 text-red-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}>{c.status}</span>
                                <span className="text-xs text-gray-400 w-20 text-right shrink-0">{c.obligation}</span>
                                <ChevronRight className="w-3.5 h-3.5 text-gray-300 mt-0.5 shrink-0" />
                              </div>
                              {blockedReason && (
                                <p className="text-xs text-gray-400 ml-[7.5rem] mt-0.5">{blockedReason}</p>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Download */}
          <div className="flex justify-end">
            <button onClick={downloadJson}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50">
              <Download className="w-4 h-4" /> Download signed result (JSON)
            </button>
          </div>
        </>
      )}

      {selectedCase && (
        <CertCaseModal reportCase={selectedCase} onClose={() => setSelectedCase(null)} />
      )}
    </div>
  );
}
